import { CustomSelect } from "@/components/ui/Select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import type { UserProfile } from "@/types/user";
import type { Team } from "@/types/task";

interface TaskFormAssignmentFieldsProps {
  formData: {
    assignee_type?: string;
    assignee?: string;
  };
  errors: Record<string, string>;
  users: UserProfile[];
  teams: Team[];
  isViewMode: boolean;
  usersLoading: boolean;
  teamsLoading: boolean;
  onAssigneeTypeChange: (value: string) => void;
  onAssigneeChange: (value: string) => void;
}

export const TaskFormAssignmentFields: React.FC<
  TaskFormAssignmentFieldsProps
> = ({
  formData,
  errors,
  users,
  teams,
  isViewMode,
  usersLoading,
  teamsLoading,
  onAssigneeTypeChange,
  onAssigneeChange,
}) => {
  return (
    <div className="box-border content-stretch flex flex-row gap-4 items-start justify-start p-0 relative shrink-0 w-full">
      {/* Assignment Type */}
      <div className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-start justify-start min-h-px min-w-px p-0 relative shrink-0">
        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
          <div
            className="font-['Open_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#444444] text-[14px] text-left text-nowrap"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <p className="block leading-[1.5] whitespace-pre">
              Assignment User
            </p>
          </div>
        </div>
        <CustomSelect
          value={formData.assignee_type || "User"}
          onValueChange={onAssigneeTypeChange}
          options={[
            { value: "User", label: "User" },
            { value: "Team", label: "Team" },
          ]}
          placeholder="Select Type"
          disabled={isViewMode}
        />
      </div>

      {/* Assignee */}
      <div className="basis-0 box-border content-stretch flex flex-col gap-1.5 grow items-start justify-start min-h-px min-w-px p-0 relative shrink-0">
        <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start p-0 relative shrink-0">
          <div
            className="font-['Open_Sans:SemiBold',_sans-serif] font-semibold leading-[0] relative shrink-0 text-[#444444] text-[14px] text-left text-nowrap"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <p className="leading-[1.5] whitespace-pre">
              <span>Assignee </span>
              <span className="text-[#e7000b]">*</span>
            </p>
          </div>
        </div>
        {formData.assignee_type === "User" ? (
          <SearchableSelect
            value={formData.assignee || ""}
            onValueChange={onAssigneeChange}
            options={[
              {
                value: "",
                label: usersLoading ? "Loading..." : "Select user",
                disabled: usersLoading,
              },
              ...users.map((user) => ({
                value: user.id,
                label: `${user.email}${
                  user.first_name || user.last_name
                    ? ` (${[user.first_name, user.last_name]
                        .filter(Boolean)
                        .join(" ")})`
                    : ""
                }`,
              })),
            ]}
            disabled={usersLoading || isViewMode}
            hasError={!!errors.assignee}
            placeholder="Select user"
            searchPlaceholder="Search users..."
          />
        ) : (
          <CustomSelect
            value={formData.assignee || ""}
            onValueChange={onAssigneeChange}
            options={[
              {
                value: "",
                label: teamsLoading ? "Loading..." : "Select team",
                disabled: teamsLoading,
              },
              ...teams.map((team) => ({
                value: team.id,
                label: team.name,
              })),
            ]}
            disabled={teamsLoading || isViewMode}
            hasError={!!errors.assignee}
            placeholder="Select team"
          />
        )}
        {errors.assignee && (
          <p className="mt-1 text-xs text-red-600">{errors.assignee}</p>
        )}
      </div>
    </div>
  );
};
